import { useState } from "react";
import type { MediaSetting } from "@circle-react/types/MediaSetting";
import { Form } from "@circle-react-uikit/Form";
import type { OnSubmitFunction } from "@circle-react-uikit/Form/Form";
import { AnalyticsTab } from "./AnalyticsTab";
import { EditTab } from "./EditTab";
import { formId } from "./constants";
import type { MediaSettingFormData, ModalTab } from "./types";

type SubmitFunction = OnSubmitFunction<MediaSettingFormData>;

interface ModalBodyProps {
  mediaSettings: MediaSetting;
  onChaptersUpdated?: (mediaSettings: MediaSetting) => void;
  onSubmit: (data: MediaSettingFormData) => Promise<MediaSetting>;
  selectedTab: ModalTab;
  signedId: string;
}

const getFormDefaultValues = (
  mediaSettings: MediaSetting,
): MediaSettingFormData => ({
  custom_thumbnail: mediaSettings.custom_thumbnail_signed_id,
  make_downloadable: mediaSettings.make_downloadable,
  transcript: {
    enabled: mediaSettings.transcript?.enabled ?? false,
    selected_file: mediaSettings.transcript?.selected_file ?? "user",
  },
  chapters: mediaSettings.chapters ?? [],
});

export const ModalBody = ({
  mediaSettings,
  onSubmit,
  selectedTab,
  signedId,
  onChaptersUpdated,
}: ModalBodyProps) => {
  const [customThumbnailUrl, setCustomThumbnailUrl] = useState<string | null>(
    mediaSettings.custom_thumbnail_url,
  );

  const handleSubmit: SubmitFunction = async (formData, { reset }) => {
    const updatedMediaSettings = await onSubmit(formData);

    setCustomThumbnailUrl(updatedMediaSettings.custom_thumbnail_url);
    reset(getFormDefaultValues(updatedMediaSettings));
    onChaptersUpdated?.(updatedMediaSettings);
  };

  return (
    <Form
      id={formId}
      className="h-full"
      formClassName="h-full"
      defaultValues={getFormDefaultValues(mediaSettings)}
      onSubmit={handleSubmit}
    >
      {selectedTab === "analytics" && <AnalyticsTab signedId={signedId} />}
      {selectedTab === "edit" && (
        <EditTab
          mediaSettings={mediaSettings}
          customThumbnailUrl={customThumbnailUrl}
          onCustomThumbnailUrlChange={setCustomThumbnailUrl}
        />
      )}
    </Form>
  );
};
